import { IPageContent } from "api/interfaces/static-content.type";
import { RootState } from "interfaces/generics.type";
import { useTranslation } from "next-i18next";
import dynamic from "next/dynamic";
import {
  createElement, forwardRef, memo, useEffect, useImperativeHandle, useState,
} from "react";
import { useSelector } from "react-redux";
import HideIf from "util/HideIf";
import { isApparelProduct } from "util/product/apparel";
import { isKit } from "util/product/is-kit";
import { scrollTo } from "util/scroll-helper";
import { getDisplaySectionList } from "../../../../util/overview-helper";
import OverviewItem from "./OverviewItem";
import Faq from "./faq/Faq";
import MainOverview from "./overview-sections/MainOverview";
import styles from "./styles.module.scss";

const Specification = dynamic(() => import(/* webpackChunkName: "product-m-specification" */"./overview-sections/Specification"));
const SizeChart = dynamic(() => import(/* webpackChunkName: "size-chart" */"./overview-sections/SizeChart"));
const DynamicCmsContent = dynamic(() => import(/* webpackChunkName: "dynamic-cms-content" */"./overview-sections/DynamicCmsContent"));
const OeCrossReference = dynamic(() => import(/* webpackChunkName: "product-m-oe-cross" */"./overview-sections/OeCrossReference"));
const ProductReviewsDesktop = dynamic(() => import(/* webpackChunkName: "product-m-reviews" */"../reviews/product-reviews-component"));
// const ProductQuestionAnswers = dynamic(() => import(/* webpackChunkName: "product-m-questionanswer" */"component/product/V2/questionanswer/ProductQuestionAnswers"));
const FittingVehicle = dynamic(() => import(/* webpackChunkName: "product-m-fitting-vehicle" */ "./overview-sections/FittingVehicles"));
// const ProductReviewSiteZabber = dynamic(() => import(/* webpackChunkName: "product-review-sitejabber" */"../../../../modules/third-party/sitejabber/ProductReviews"));
const ProductHighlightsV2 = dynamic(() => import(/* webpackChunkName: "product-highlights-v2" */"component/product/V2/product-highlights/ProductHighlightsV2"));
const WheelTireHighlights = dynamic(() => import(/* webpackChunkName: "wheel-tire-highlights" */"component/product/V2/wheel-tire-highlights/WheelTireHighlights"));
const InstallationTips = dynamic(() => import(/* webpackChunkName: "installation-tips" */"./overview-sections/InstallationTips"));
const VehicleFitment = dynamic(() => import(/* webpackChunkName: "vehicle-fitment" */"modules/fitment/VehicleFitment"));

export const KEY_TO_STATE_MAP = {
  FITTINGMODELS: "fittingModelsOpen",
  SPECIFICATION: "specificationOpen",
  FITTINGVEHICLE: "fittingVehicleOpen",
  OECROSSREFERENCE: "oeCrossReferenceOpen",
  REVIEWS: "reviewsOpen",
  // QUESTIONANSWER: "questionsAnswerOpen",
  OVERVIEW: "overviewOpen",
  FAQ: "faqOpen",
  PRODUCTHIGHLIGHTS: "productHighlightsOpen",
  INSTALLATIONTIPS: "installationTipsOpen",
  VEHICLEFITMENT: "vehicleFitmentOpen",
  SIZECHART: "sizeChartOpen",
  CAREGUIDE: "careGuideOpen",
  DIMENSIONS: "dimensionOpen",
};

interface IProps {
  staticContent: IPageContent;
}

const ProductOverview = forwardRef((props: IProps, ref: any) => {
  const {
    product = {}, details,
    // productReviews: reviews,
    productQuestions: questionAnswers,
    staticContent, handleProductOverviewSectionExpand,
    maximumPrice,
    minimumPrice,
    manufactureImageUrl,
    manufacturer,
    thumbnail,
    description,
    getThumbImagePath,
    manufacturerPageUrl,
    handleAddToCart,
    installationTips,
    showVehicleFitment,
    fitmentOptionsDetails,
    isCompact,
    productReviews,
    omitSections = [],
  } = props;
  const { t } = useTranslation("product");
  const { overviewId } = useSelector((store: RootState) => store.utils);
  const isWheelTireCategory = ["Tire", "Wheel", t("Tire"), t("Wheel")].includes(product.description);

  const initialState = {
    overviewOpen: isKit(product),
    vehicleFitmentOpen: false,
    fittingModelsOpen: false,
    specificationOpen: false,
    fittingVehicleOpen: false,
    oeCrossReferenceOpen: false,
    reviewsOpen: false,
    questionsAnswerOpen: false,
    faqOpen: false,
    productHighlightsOpen: true,
    installationTipsOpen: false,
    sizeChartOpen: false,
    careGuideOpen: false,
    dimensionOpen: false,
  };
  const [state, setState] = useState(initialState);

  useEffect(() => {
    if (window.innerWidth >= 768) {
      setState({
        ...initialState,
      });
    }
  }, []);

  // When overviewId is changed via link, state should be updated
  useEffect(() => {
    if (KEY_TO_STATE_MAP[overviewId]) {
      setState({ ...state, [KEY_TO_STATE_MAP[overviewId]]: true });
    }
  }, [overviewId]);
  // The component instance will be extended
  // with whatever you return from the callback passed
  // as the second argument
  useImperativeHandle(ref, () => ({
    expandSection(section: any) {
      setState({ ...state, [KEY_TO_STATE_MAP[section]]: true });
      setTimeout(() => {
        scrollTo(section, {
          duration: 200,
          delay: 0,
          offset: -200,
          smooth: true,
        });
      }, 200);
    },
  }));

  function closeSection(section: any) {
    setState({ ...state, [KEY_TO_STATE_MAP[section]]: false });
  }

  function handleFittingVehicleExpand() {
    setState({ ...state, [KEY_TO_STATE_MAP.FITTINGVEHICLE]: true });
    setTimeout(() => document.getElementById("FITTINGVEHICLE")?.scrollIntoView({ behavior: "smooth" }), 500);
  }

  const { price, rating, extendedDescription } = product || {};
  const isApparel = isApparelProduct(product);
  const overviewItemProps = {
    thumbnail: thumbnail || product.thumbnail,
    description: description || product.description,
    getThumbImagePath,
    price,
    rating,
    maximumPrice,
    minimumPrice,
    onClosing: closeSection,
  };

  const overViewSectionArray = getDisplaySectionList({ ...product, installationTips }, staticContent, state, showVehicleFitment, isCompact, isWheelTireCategory, omitSections);
  const filterFaqSection = overViewSectionArray.find((o) => o.id === "FAQ") || {};
  const { filterFaq = [] } = filterFaqSection;
  const {
    sizeChart, careGuide, dimensions, handling,
  } = staticContent.links || {};

  const SECTION_COMPONENT_MAP = {
    OVERVIEW: MainOverview,
    VEHICLEFITMENT: VehicleFitment,
    SPECIFICATION: Specification,
    FITTINGVEHICLE: FittingVehicle,
    OECROSSREFERENCE: OeCrossReference,
    REVIEWS: ProductReviewsDesktop,
    // QUESTIONANSWER: ProductQuestionAnswers,
    PRODUCTHIGHLIGHTS: isWheelTireCategory ? WheelTireHighlights : ProductHighlightsV2,
    FAQ: Faq,
    INSTALLATIONTIPS: InstallationTips,
    SIZECHART: SizeChart,
    CAREGUIDE: DynamicCmsContent,
    DIMENSIONS: DynamicCmsContent,
    HANDLING: DynamicCmsContent,
  };

  const SPECAIAL_PROPS = {
    OVERVIEW: {
      manufacturerPageUrl,
    },
    FITTINGMODELS: {},
    SPECIFICATION: {},
    FITTINGVEHICLE: {},
    OECROSSREFERENCE: {},
    REVIEWS: { productReviews },
    QUESTIONANSWER: {},
    FAQ: { filterFaq },
    PRODUCTHIGHLIGHTS: {
      showPopover: true,
      productHighlight: true,
      width: isApparel ? 40 : null,
      height: isApparel ? 25 : null,
    },
    VEHICLEFITMENT: {
      fitmentOptionsDetails,
    },
    SIZECHART: { sizeChart },
    CAREGUIDE: { dynamicContent: careGuide },
    DIMENSIONS: { dynamicContent: dimensions, imgWidth: 500, imgHeight: 605 },
    HANDLING: { dynamicContent: handling, imgWidth: 100, imgHeight: 100 },
  };

  const OVERVIEW_ITEM_PROPS = {
    OVERVIEW: {},
    FITTINGMODELS: {},
    SPECIFICATION: {},
    FITTINGVEHICLE: {},
    OECROSSREFERENCE: {},
    REVIEWS: {
      hideMobileHeader: true,
    },
    QUESTIONANSWER: {},
    FAQ: {},
  };

  const commonProps = {
    contentIds: staticContent?.links?.contentIds,
    description: description || product.description,
    product,
    details,
    staticContent,
    handleProductOverviewSectionExpand: handleProductOverviewSectionExpand || handleFittingVehicleExpand,
    manufactureImageUrl,
    manufacturer,
    extendedDescription,
    questionAnswers,
    hideMobileHeader: true,
    installationTips,
  };

  return (
    <div className={styles.container}>
      {overViewSectionArray.map((eachOverViewItem, idx) => (
        <div key={eachOverViewItem.title}>
          <HideIf show={idx === 0}>
            <hr className={styles.overview_hr} />
          </HideIf>
          <OverviewItem
            {...eachOverViewItem}
            title={t(eachOverViewItem.title)}
            key={eachOverViewItem.title}
            {...overviewItemProps}
            lazyRender={"lazyRender" in eachOverViewItem ? eachOverViewItem.lazyRender : true}
            open={eachOverViewItem.sectionToOpen}
            {...OVERVIEW_ITEM_PROPS[eachOverViewItem.id]}
            product={product}
            staticContent={staticContent}
            handleAddToCart={handleAddToCart}
          >
            {
              createElement(SECTION_COMPONENT_MAP[eachOverViewItem.id], {
                ...commonProps,
                ...eachOverViewItem,
                ...SPECAIAL_PROPS[eachOverViewItem.id],
              })
            }
          </OverviewItem>
          <hr className={styles.overview_hr} />
        </div>
      ))}

    </div>
  );
});

ProductOverview.displayName = "ProductOverview";
export default memo(ProductOverview);
